import { useLocale } from '@/components/Link'
import { localeDate } from '@/components/page-specific/catering/common/helpers/date'
import { numberFormat } from '@/helpers/numbers'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, IconButton, Stack, Typography, alpha } from '@mui/material'

import { parseISO } from 'date-fns/parseISO'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { PastOrderForReorder } from './use-past-orders'

interface ReorderCardProps {
  order: PastOrderForReorder
  onLoad: () => void
  disabled: boolean
  currencySymbol: string
  taxAcronym: string
}

const formatMoney = (cents: number | null | undefined, symbol: string): string => {
  if (cents == null || Number.isNaN(cents)) return `${symbol}0`
  return `${symbol}${numberFormat(cents / 100)}`
}

// Use parseISO so date-only strings ('YYYY-MM-DD') resolve to local midnight,
// matching the rest of the marketplace (useShoppingCart, useMenuItemCard). Raw
// `new Date(isoString)` treats date-only inputs as UTC, shifting the displayed
// date by a day for users west of UTC.
const formatDate = (iso: string, locale: string | undefined): string => {
  try {
    return localeDate(parseISO(iso), 'd MMM yyyy', locale)
  } catch {
    return iso
  }
}

export const ReorderCard: React.FC<ReorderCardProps> = ({
  order,
  onLoad,
  disabled,
  currencySymbol,
  taxAcronym,
}) => {
  const [expanded, setExpanded] = useState(false)
  const { t } = useTranslation()
  const locale = useLocale()

  const itemCount = order.orderItems.items.length
  const totalCents = order.orderItems.items.reduce(
    (sum, item) => sum + (item.subtotalExTax ?? 0),
    0,
  )
  const totalLabel = formatMoney(totalCents, currencySymbol)
  // Show "{name} | {orderId}" when the order was named, otherwise just the
  // order number (e.g. "Christmas Lunch | ORD-123" vs "ORD-123"). This sits on
  // its own full-width row so it doesn't get clipped by the actions.
  const customName = order.customName?.trim()
  const displayName = customName ? `${customName} | ${order.orderId}` : order.orderId
  const dateLabel = formatDate(order.deliveryDate, locale)

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        transition: 'border-color .15s',
        '&:hover': {
          borderColor: 'primary.light',
        },
      }}>
      <Stack
        spacing={0.75}
        sx={{
          padding: '10px 12px',
          cursor: 'pointer',
        }}>
        {/* Row 1: heading ("{name} | {orderId}") on its own full-width line.
            Wraps rather than truncating so the order number is never clipped. */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: 12,
            overflowWrap: 'anywhere',
          }}>
          {displayName}
        </Typography>

        {/* Row 2: meta on the left (date, then item count · value on the line
            below), expand + Load actions vertically centred on the right. */}
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Stack flex={1} minWidth={0}>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
              {dateLabel}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
              {t('marketplace:itemsCount', { count: itemCount })} · {totalLabel}{' '}
              {t('marketplace:ex', { taxAcronym })}
            </Typography>
          </Stack>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setExpanded((v) => !v)
            }}
            sx={{
              width: 24,
              height: 24,
              border: '1.5px solid',
              borderColor: 'divider',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform .2s, border-color .15s',
              '&:hover': { borderColor: 'primary.main' },
            }}
            aria-label={
              expanded ? t('marketplace:reorderCollapseItems') : t('marketplace:reorderExpandItems')
            }>
            <ExpandMoreIcon sx={{ fontSize: 13 }} />
          </IconButton>

          <Button
            variant="outlined"
            size="small"
            onClick={onLoad}
            disabled={disabled}
            sx={{
              borderRadius: '100px',
              padding: '5px 11px',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'none',
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
              },
            }}>
            {t('marketplace:reorderLoad')}
          </Button>
        </Stack>
      </Stack>

      {expanded && (
        <Box
          sx={{
            padding: '8px 12px 10px',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}>
          {order.orderItems.items.map((item) => (
            <Stack
              key={item.id}
              direction="row"
              justifyContent="space-between"
              sx={{ fontSize: 11, lineHeight: 1.9 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {item.name} ×{item.qty ?? 1}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {formatMoney(item.subtotalExTax, currencySymbol)}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Box>
  )
}
